import { Portal, useToast } from '@chakra-ui/react'
import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons'
import {
  faCaretDown,
  faCheck,
  faExclamationTriangle,
  faSearch,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAppKitAccount } from '@reown/appkit/react'
import type React from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  useCreateSystemWalletMutation,
  useCreateWalletMutation,
  useGetNetworksQuery,
} from '../redux/services/coreApi'
import { RootState } from '../redux/store'
import type { NetworkDto, WalletHostingType } from '../types/coreApi-types'
import DefaultModal from './DefaultModal'
import Icon from './Icon'
import Toast from './Toast'
import Tooltip from './Tooltip'

interface AddWalletModalProps {
  isOpen: boolean
  onClose: () => void
  initialAddress?: string
  systemWallet?: boolean
}

const AddWalletModal: React.FC<AddWalletModalProps> = ({
  isOpen,
  onClose,
  initialAddress,
  systemWallet = false,
}) => {
  const sessionUser = useSelector((state: RootState) => state.session.user)
  const toast = useToast()

  const { data: networksData } = useGetNetworksQuery()
  const [createWallet, { isLoading: isLoadingWallet }] =
    useCreateWalletMutation()
  const [createSystemWallet, { isLoading: isLoadingSystemWallet }] =
    useCreateSystemWalletMutation()

  const isLoading = isLoadingWallet || isLoadingSystemWallet

  const [selectedNetwork, setSelectedNetwork] = useState<NetworkDto | null>(
    null
  )
  const [name, setName] = useState<string>('')
  const [address, setAddress] = useState<string>('')
  const [destinationTag, setDestinationTag] = useState<string>('')
  const [hostingType, setHostingType] = useState<WalletHostingType>('Unknown')
  const [hostedVaspId, setHostedVaspId] = useState<string>('')
  const [networkSearchTerm, setNetworkSearchTerm] = useState<string>('')
  const [isNetworkDropdownOpen, setIsNetworkDropdownOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  })

  // WalletConnect integration
  const { address: connectedAddress } = useAppKitAccount()

  // Filter networks based on search term
  const filteredNetworks =
    networksData?.filter(network =>
      network.name.toLowerCase().includes(networkSearchTerm.toLowerCase())
    ) || []

  const createWalletInputError =
    address.length === 0 || selectedNetwork === null

  const onSubmit = async () => {
    if (!selectedNetwork) return

    if (systemWallet) {
      await createSystemWallet({
        name: name || null,
        address,
        destinationTag,
        networkId: selectedNetwork.id,
      }).unwrap()
    } else {
      await createWallet({
        userId: sessionUser.id,
        body: {
          name: name || null,
          address,
          destinationTag,
          networkId: selectedNetwork.id,
          hostingType: hostingType !== 'Unknown' ? hostingType : null,
          hostedVaspId: hostedVaspId || null,
        },
      }).unwrap()
      toast({
        position: 'bottom-right',
        render: () => (
          <Toast type="success">Wallet added! Screening is in progress.</Toast>
        ),
      })
    }
    onClose()
  }

  useEffect(() => {
    if (isOpen) {
      setName('')
      setAddress(initialAddress || '')
      setDestinationTag('')
      setHostingType('Unknown')
      setHostedVaspId('')
      setSelectedNetwork(null)
      setNetworkSearchTerm('')
      setIsNetworkDropdownOpen(false)
    }
  }, [isOpen, initialAddress])

  const getDisplayText = (type: WalletHostingType) => {
    if (type === 'SelfHosted') return 'Self hosted'
    if (type === 'VaspHosted') return 'Exchange hosted'
    return 'Unknown'
  }

  const getTooltipText = (type: WalletHostingType) => {
    if (type === 'SelfHosted') {
      return 'Your wallet is stored locally on your device using apps like MetaMask, Trust Wallet, or other self-custody solutions.'
    }
    if (type === 'VaspHosted') {
      return 'Your wallet is hosted by a cryptocurrency exchange (like Coinbase, Binance, Kraken, etc.) or another third-party service that holds your funds.'
    }
    return 'Unknown wallet hosting type.'
  }

  // Handle click outside to close dropdown. The menu is rendered in a portal
  // (outside dropdownRef), so check both the trigger and the menu.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (dropdownRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setIsNetworkDropdownOpen(false)
    }

    if (isNetworkDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isNetworkDropdownOpen])

  // Position the portaled dropdown directly below the trigger and keep it
  // anchored while the modal (or window) scrolls/resizes.
  useLayoutEffect(() => {
    if (!isNetworkDropdownOpen) return

    const updatePosition = () => {
      const rect = dropdownRef.current?.getBoundingClientRect()
      if (rect) {
        setMenuPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
        })
      }
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isNetworkDropdownOpen])

  return (
    <DefaultModal
      isLoading={isLoading}
      isLoadingText="Adding your wallet..."
      isOpen={isOpen}
      onClose={() => {
        setName('')
        setSelectedNetwork(null)
        setAddress('')
        setHostingType('Unknown')
        setHostedVaspId('')
        setNetworkSearchTerm('')
        setIsNetworkDropdownOpen(false)
        onClose()
      }}
      title="Add wallet"
    >
      <div className="p-5 pt-0 space-y-5 overflow-y-auto max-h-[80vh]">
        {connectedAddress && (
          <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-md mb-4">
            <p className="text-description mb-1">Connected Wallet</p>
            <p className="text-sm font-semi truncate">{connectedAddress}</p>
          </div>
        )}

        <div>
          <p className="mb-1 text-description">Wallet name (optional)</p>
          <input
            type="text"
            className="input-card"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Enter a name for this wallet"
          />
        </div>

        <div>
          <p className="mb-1 text-description">Wallet address</p>
          <input
            type="text"
            className="input-card"
            value={address}
            onChange={e => setAddress(e.target.value)}
          />
        </div>

        <div>
          <div className="flex items-center gap-1 mb-1">
            <p className="text-description">Destination tag / memo</p>
            <Tooltip label="Destination tags and memos are optional identifiers used by certain networks (like XRP, XLM, etc.) to route transactions to the correct recipient. Only fill this field if the network you're using requires it or if you need to specify a particular destination.">
              <FontAwesomeIcon
                icon={faQuestionCircle}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              />
            </Tooltip>
          </div>
          <input
            type="text"
            className="input-card"
            value={destinationTag}
            onChange={e => setDestinationTag(e.target.value)}
            placeholder="Optional - only for networks that require it"
          />
        </div>

        <div className="flex flex-col">
          <p className="text-description mb-1">Network</p>
          <div ref={dropdownRef} className="relative">
            {/* Network Selection Input */}
            <div
              className="input-card text-start flex items-center pl-3 gap-2 cursor-pointer"
              onClick={() => setIsNetworkDropdownOpen(!isNetworkDropdownOpen)}
            >
              <Icon src={selectedNetwork} size="base" />
              {selectedNetwork?.name || (
                <p className="text-description">Select a network</p>
              )}
              <div className="absolute right-0 top-0 bottom-0 flex items-center mr-3 text-brand-light-text-description">
                <FontAwesomeIcon
                  icon={faCaretDown}
                  size="sm"
                  className={`transition-transform duration-200 ${
                    isNetworkDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </div>
            </div>

            {/* Custom Dropdown — portaled so it floats above the modal's
                overflow-y-auto scroll container instead of being clipped. */}
            {isNetworkDropdownOpen && (
              <Portal>
                <div
                  ref={menuRef}
                  style={{
                    position: 'fixed',
                    top: menuPosition.top,
                    left: menuPosition.left,
                    width: menuPosition.width,
                  }}
                  className="z-[2000] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-80 overflow-hidden"
                >
                  {/* Search Input */}
                  <div className="p-3 border-b border-gray-200 dark:border-gray-600">
                    <div className="relative">
                      <input
                        ref={searchInputRef}
                        type="text"
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Search networks..."
                        value={networkSearchTerm}
                        onChange={e => setNetworkSearchTerm(e.target.value)}
                        autoFocus
                      />
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm pointer-events-none"
                      />
                    </div>
                  </div>

                  {/* Network List */}
                  <div className="max-h-60 overflow-y-auto">
                    {filteredNetworks.length > 0 ? (
                      filteredNetworks.map(network => (
                        <div
                          key={network.id}
                          className={`flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-l-2 ${
                            network === selectedNetwork
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                              : 'border-transparent'
                          }`}
                          onClick={() => {
                            setSelectedNetwork(network)
                            setIsNetworkDropdownOpen(false)
                            setNetworkSearchTerm('')
                          }}
                        >
                          <Icon src={network} size="md" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {network.name}
                            </p>
                            {network.nativeCurrency?.isoCode && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {network.nativeCurrency.isoCode}
                              </p>
                            )}
                          </div>
                          {network === selectedNetwork && (
                            <FontAwesomeIcon
                              icon={faCheck}
                              className="text-blue-500 text-sm"
                            />
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                        No networks found
                      </div>
                    )}
                  </div>
                </div>
              </Portal>
            )}
          </div>
        </div>

        {!systemWallet && (
          <>
            <div>
              <p className="mb-3 text-description">
                Wallet Hosting Type{' '}
                <span className="text-gray-400 text-xs">(Optional)</span>
              </p>
              <div className="space-y-3">
                {(['SelfHosted', 'VaspHosted'] as WalletHostingType[]).map(
                  type => (
                    <label
                      key={type}
                      className="flex items-start gap-3 cursor-pointer p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      <input
                        type="radio"
                        name="hostingType"
                        value={type}
                        checked={hostingType === type}
                        onChange={e =>
                          setHostingType(e.target.value as WalletHostingType)
                        }
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">
                            {getDisplayText(type)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {getTooltipText(type)}
                        </p>
                      </div>
                    </label>
                  )
                )}
              </div>
            </div>

            {hostingType === 'VaspHosted' && (
              <div>
                <p className="mb-2 text-description">
                  Exchange/Service Name{' '}
                  <span className="text-gray-400">(Optional)</span>
                </p>
                <input
                  type="text"
                  value={hostedVaspId}
                  onChange={e => setHostedVaspId(e.target.value)}
                  placeholder="e.g., Coinbase, Binance, Kraken"
                  className="input-card w-full"
                />
              </div>
            )}

            {hostingType === 'Unknown' && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0"
                  />
                  <div>
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Optional but Recommended
                    </p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                      You can add the wallet without declaring the hosting type,
                      but you'll need to declare it before using it for
                      cryptocurrency transfers. This information is required for
                      compliance and security purposes.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <button
          disabled={createWalletInputError}
          onClick={onSubmit}
          className="button"
        >
          Add wallet
        </button>
      </div>
    </DefaultModal>
  )
}

export default AddWalletModal
